import React, { useEffect, useRef, useCallback } from "react";
import type { Pos, Direction, FuelGrid, GamePhase } from "../types.ts";
import { useCanvas } from "../hooks/use-canvas.ts";
import { drawGrid, hitTest } from "../canvas/grid-renderer.ts";

interface GameBoardProps {
  grid: FuelGrid;
  playerPos: Pos | null;
  phase: GamePhase;
  onSelectStart: (pos: Pos) => void;
  onMove: (pos: Pos) => void;
  onMoveDir: (dir: Direction) => void;
}

export function GameBoard({
  grid, playerPos, phase, onSelectStart, onMove, onMoveDir,
}: GameBoardProps) {
  const canvasRef = useCanvas(
    (ctx, w, h) => drawGrid(ctx, grid, playerPos, phase, w, h),
    [grid, playerPos, phase]
  );

  // Click handling
  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      const canvas = e.currentTarget;
      const rect = canvas.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      const pos = hitTest(grid, rect.width, rect.height, x, y);
      if (!pos) return;

      if (phase === "starting") {
        onSelectStart(pos);
      } else if (phase === "playing") {
        onMove(pos);
      }
    },
    [grid, phase, onSelectStart, onMove]
  );

  // Keyboard
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (phase !== "playing") return;
      const dirMap: Record<string, Direction> = {
        ArrowUp: "up", ArrowDown: "down", ArrowLeft: "left", ArrowRight: "right",
        w: "up", s: "down", a: "left", d: "right",
        W: "up", S: "down", A: "left", D: "right",
      };
      const dir = dirMap[e.key];
      if (dir) {
        e.preventDefault();
        onMoveDir(dir);
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [phase, onMoveDir]);

  // Touch swipe
  const touchStart = useRef<{ x: number; y: number } | null>(null);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const t = e.touches[0];
    if (t) touchStart.current = { x: t.clientX, y: t.clientY };
  }, []);

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (!touchStart.current) return;
      const t = e.changedTouches[0];
      if (!t) return;

      const dx = t.clientX - touchStart.current.x;
      const dy = t.clientY - touchStart.current.y;
      touchStart.current = null;

      const threshold = 30;
      if (Math.abs(dx) < threshold && Math.abs(dy) < threshold) {
        // Tap — handle as click for starting phase
        if (phase === "starting") {
          const canvas = e.currentTarget as HTMLCanvasElement;
          const rect = canvas.getBoundingClientRect();
          const pos = hitTest(grid, rect.width, rect.height, t.clientX - rect.left, t.clientY - rect.top);
          if (pos) onSelectStart(pos);
        }
        return;
      }

      if (phase !== "playing") return;
      if (Math.abs(dx) > Math.abs(dy)) {
        onMoveDir(dx > 0 ? "right" : "left");
      } else {
        onMoveDir(dy > 0 ? "down" : "up");
      }
    },
    [grid, phase, onSelectStart, onMoveDir]
  );

  return (
    <canvas
      ref={canvasRef}
      className="w-full aspect-square max-w-[480px] cursor-pointer"
      onClick={handleClick}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    />
  );
}
